// src/app/core/services/learning.service.ts

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  CreateLearningPathDto,
  LearningPath,
  ProfessorAssignmentDto,
  UpdateLearningPathDto,
} from '../interfaces/learning-path';
import { ApiResponse } from '../interfaces/responses/api-response';
import {
  CreateSubjectDto,
  Subject,
  UpdateSubjectDto,
} from '../interfaces/subject';
import { CreateThemeDto, Theme, UpdateThemeDto } from '../interfaces/theme';
import { Question } from '../interfaces/question';
import {
  CreateProfessorDto,
  ProfessorAssignment,
  ProfessorLearningPathLightDto,
  ProfessorSubjectLightDto,
} from '../interfaces/professor';

@Injectable({
  providedIn: 'root',
})
export class LearningService {
  private http = inject(HttpClient);
  private baseURL = environment.BASE_URL;

  // ============ Learning Paths ============

  /**
   * Les parcours, pour une liste où l'on **choisit** : créer un test, filtrer
   * une bibliothèque, affecter un professeur, composer un groupe.
   *
   * <p>Le serveur n'y renvoie que les matières actives — c'est le défaut de
   * cette méthode, parce qu'un appelant qui oublie le paramètre doit tomber du
   * bon côté : une matière désactivée ne se propose nulle part. Les deux
   * écrans qui la montrent quand même — « Parcours et matières », qui la
   * réactive, et les packs, qui la tarifent — passent par
   * {@link getReferential}.</p>
   */
  getLearningPaths(
    includeSubjects = true,
    includeThemes = true,
    activeOnly = true,
  ): Observable<ApiResponse<LearningPath[]>> {
    const params = new HttpParams()
      .set('includeSubjects', includeSubjects.toString())
      .set('includeThemes', includeThemes.toString())
      .set('activeOnly', activeOnly.toString());

    return this.http.get<ApiResponse<LearningPath[]>>(
      `${this.baseURL}/learnings/paths`,
      { params },
    );
  }

  /**
   * Le référentiel entier : tous les parcours, toutes leurs matières, actives
   * ou non.
   *
   * <p>À réserver à ce qui administre les matières, et à ce qui doit
   * **nommer** une donnée existante — un test, un groupe ou une inscription
   * dont la matière a été désactivée depuis garde son libellé et son
   * parcours. Une liste de choix construite dessus passe par
   * `selectableSubjects`.</p>
   */
  getReferential(includeThemes = false): Observable<ApiResponse<LearningPath[]>> {
    return this.getLearningPaths(true, includeThemes, false);
  }

  /**
   * Parcours visibles par l'utilisateur courant, avec leurs matières.
   *
   * <p>Un administrateur voit tout le référentiel. Un professeur ne doit voir
   * que ce qui lui est affecté : la banque de questions, la liste de ses tests
   * et sa bibliothèque proposaient jusqu'ici <em>tous</em> les parcours et
   * <em>toutes</em> les matières de l'établissement, y compris ceux qu'il
   * n'enseigne pas. Choisir dans cette liste ne menait nulle part, et elle
   * révélait au passage l'organisation complète de l'école.</p>
   *
   * <p>La restriction se fait ici plutôt que dans chaque écran : trois filtrages
   * écrits séparément auraient fini par diverger, et le quatrième écran aurait
   * oublié d'en avoir un.</p>
   */
  getScopedLearningPaths(
    userId: string | null,
    isAdmin: boolean,
    includeSubjects = true,
    includeThemes = true,
  ): Observable<ApiResponse<LearningPath[]>> {
    const all$ = this.getLearningPaths(includeSubjects, includeThemes);

    // Sans identité utilisable, on ne devine pas un périmètre : mieux vaut
    // rendre le référentiel entier que d'afficher une liste vide au professeur.
    if (isAdmin || !userId) return all$;

    return forkJoin({
      all: all$,
      mine: this.getProfessorLearningPath(userId).pipe(
        catchError(() => of(null as ApiResponse<ProfessorLearningPathLightDto[]> | null)),
      ),
    }).pipe(
      switchMap(({ all, mine }) => {
        const allowedPaths = mine?.data ?? null;

        // Affectations illisibles : on n'invente pas un périmètre. Le
        // référentiel complet passe, comme avant — un écran vide serait pire
        // qu'un écran trop large, et l'échec se voit ailleurs.
        if (!allowedPaths) return of(all);

        const allowedIds = new Set(allowedPaths.map((p) => p.id));
        const paths = (all?.data ?? []).filter((p) => allowedIds.has(p.id));

        if (paths.length === 0) return of({ ...all, data: paths });

        // Les matières se demandent parcours par parcours : c'est le seul
        // endpoint qui connaisse l'affectation à la matière, plus fine que
        // l'affectation au parcours.
        return forkJoin(
          paths.map((p) =>
            this.getProfessorSubjects(userId, p.id).pipe(
              map((res) => ({ pathId: p.id, subjectIds: (res?.data ?? []).map((s) => s.id) })),
              catchError(() => of({ pathId: p.id, subjectIds: null as number[] | null })),
            ),
          ),
        ).pipe(
          map((scopes) => {
            const byPath = new Map(scopes.map((s) => [s.pathId, s.subjectIds]));
            return {
              ...all,
              data: paths.map((p) => {
                const allowedSubjects = byPath.get(p.id);
                // `null` = la requête a échoué pour ce parcours : on garde ses
                // matières plutôt que d'en masquer par accident.
                if (!allowedSubjects) return p;
                const keep = new Set(allowedSubjects);
                return { ...p, subjects: (p.subjects ?? []).filter((s) => keep.has(s.id)) };
              }),
            };
          }),
        );
      }),
    );
  }

  getLearningPathById(
    pathId: number,
    includeSubjects = true,
    includeThemes = true,
    activeOnly = true,
  ): Observable<ApiResponse<LearningPath>> {
    const params = new HttpParams()
      .set('includeSubjects', includeSubjects.toString())
      .set('includeThemes', includeThemes.toString())
      .set('activeOnly', activeOnly.toString());

    return this.http.get<ApiResponse<LearningPath>>(
      `${this.baseURL}/learnings/paths/${pathId}`,
      { params },
    );
  }

  createLearningPath(
    dto: CreateLearningPathDto,
  ): Observable<ApiResponse<LearningPath>> {
    return this.http.post<ApiResponse<LearningPath>>(
      `${this.baseURL}/learnings/paths`,
      dto,
    );
  }

  updateLearningPath(
    pathId: number,
    dto: UpdateLearningPathDto,
  ): Observable<ApiResponse<LearningPath>> {
    return this.http.put<ApiResponse<LearningPath>>(
      `${this.baseURL}/learnings/paths/${pathId}`,
      dto,
    );
  }

  deleteLearningPath(pathId: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(
      `${this.baseURL}/learnings/paths/${pathId}`,
    );
  }

  // ============ Subjects ============

  /**
   * Les matières d'un parcours — les seules actives par défaut, comme
   * {@link getLearningPaths} : c'est une liste de choix. `activeOnly` à
   * `false` sert au formulaire qui doit aussi montrer ce qui est déjà
   * affecté.
   */
  getSubjectsByLearningPath(
    pathId: number,
    includeThemes = true,
    activeOnly = true,
  ): Observable<ApiResponse<Subject[]>> {
    const params = new HttpParams()
      .set('includeThemes', includeThemes.toString())
      .set('activeOnly', activeOnly.toString());

    return this.http.get<ApiResponse<Subject[]>>(
      `${this.baseURL}/learnings/paths/${pathId}/subjects`,
      { params },
    );
  }

  getSubjectById(
    subjectId: number,
    includeThemes = true,
  ): Observable<ApiResponse<Subject>> {
    const params = new HttpParams().set(
      'includeThemes',
      includeThemes.toString(),
    );

    return this.http.get<ApiResponse<Subject>>(
      `${this.baseURL}/learnings/subjects/${subjectId}`,
      { params },
    );
  }

  createSubject(dto: CreateSubjectDto): Observable<ApiResponse<Subject>> {
    return this.http.post<ApiResponse<Subject>>(
      `${this.baseURL}/learnings/subjects`,
      dto,
    );
  }

  updateSubject(
    subjectId: number,
    dto: UpdateSubjectDto,
  ): Observable<ApiResponse<Subject>> {
    return this.http.put<ApiResponse<Subject>>(
      `${this.baseURL}/learnings/subjects/${subjectId}`,
      dto,
    );
  }

  deleteSubject(subjectId: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(
      `${this.baseURL}/learnings/subjects/${subjectId}`,
    );
  }

  // ============ Themes ============

  getThemesBySubject(subjectId: number): Observable<ApiResponse<Theme[]>> {
    return this.http.get<ApiResponse<Theme[]>>(
      `${this.baseURL}/learnings/subjects/${subjectId}/themes`,
    );
  }

  getThemeById(themeId: number): Observable<ApiResponse<Theme>> {
    return this.http.get<ApiResponse<Theme>>(
      `${this.baseURL}/learnings/themes/${themeId}`,
    );
  }

  createTheme(dto: CreateThemeDto): Observable<ApiResponse<Theme>> {
    return this.http.post<ApiResponse<Theme>>(
      `${this.baseURL}/learnings/themes`,
      dto,
    );
  }

  updateTheme(
    themeId: number,
    dto: UpdateThemeDto,
  ): Observable<ApiResponse<Theme>> {
    return this.http.put<ApiResponse<Theme>>(
      `${this.baseURL}/learnings/themes/${themeId}`,
      dto,
    );
  }

  deleteTheme(themeId: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(
      `${this.baseURL}/learnings/themes/${themeId}`,
    );
  }

  // ============ Questions ============

  getQuestionsByThemes(
    themeIds: number[],
  ): Observable<ApiResponse<Question[]>> {
    return this.http.post<ApiResponse<Question[]>>(
      `${this.baseURL}/questions/themes`,
      themeIds,
    );
  }

  getQuestionsByLearningPath(
    pathId: number,
  ): Observable<ApiResponse<Question[]>> {
    return this.http.get<ApiResponse<Question[]>>(
      `${this.baseURL}/questions/learningpath/${pathId}`,
    );
  }

  // ============ Professor Assignments ============

  /*createProfessor(
    dto: CreateProfessorDto,
  ): Observable<ApiResponse<CreateProfessorDto>> {
    return this.http.post<ApiResponse<CreateProfessorDto>>(
      `${this.baseURL}/professors`,
      dto,
    );
  }*/

  assignProfessorToSubject(
    dto: ProfessorAssignmentDto,
  ): Observable<ApiResponse<ProfessorAssignment>> {
    return this.http.post<ApiResponse<ProfessorAssignment>>(
      `${this.baseURL}/professors/assignments`,
      dto,
    );
  }

  getProfessorAssignments(
    professorId: string,
  ): Observable<ApiResponse<ProfessorAssignment[]>> {
    return this.http.get<ApiResponse<ProfessorAssignment[]>>(
      `${this.baseURL}/professors/${professorId}/assignments`,
    );
  }

  getProfessorLearningPath(
    professorId: string,
  ): Observable<ApiResponse<ProfessorLearningPathLightDto[]>> {
    return this.http.get<ApiResponse<ProfessorLearningPathLightDto[]>>(
      `${this.baseURL}/professors/by-user/${professorId}/paths`,
    );
  }

  getProfessorSubjects(
    professorId: string,
    learningPathId: number,
  ): Observable<ApiResponse<ProfessorSubjectLightDto[]>> {
    return this.http.get<ApiResponse<ProfessorSubjectLightDto[]>>(
      `${this.baseURL}/professors/by-user/${professorId}/paths/${learningPathId}/subjects`,
    );
  }

  getProfessors(): Observable<ApiResponse<ProfessorAssignmentDto[]>> {
    return this.http.get<ApiResponse<ProfessorAssignmentDto[]>>(
      `${this.baseURL}/professors/assignments`,
    );
  }

  removeProfessorAssignment(
    assignmentId: number,
  ): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(
      `${this.baseURL}/professors/assignments/${assignmentId}`,
    );
  }
}
